// client/src/components/pages/LexiStar/ReviewQueue/ReviewItemPanel.jsx
//
// The review-queue side panel (LS-09 §5.18): what the record is and why it is
// here (kind, confidence chip, impact, cause), the ± 5 min map evidence, the
// confidence breakdown by factor (LS-04 §14: 100 − penalties), related events
// and earlier corrections, then the one-tap outcomes:
//   Accept as recorded   POST /review-items/:id:accept   (one tap; note optional)
//   Correct              POST /review-items/:id:correct  { kind, after, reason }
//   Dismiss              POST /review-items/:id:dismiss  { reason }
// Correct and Dismiss always need a reason. Touch targets are ≥ 48 px and
// nothing depends on hover (LS-09 §1.1 field-first on the phone).

import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaPen, FaTimes } from 'react-icons/fa';
import { getReviewItem, pickList } from '../../../../services/lexistarAssurance';
import { fmtDateTime, fmtDuration, fmtTime, secondsSince } from '../StatusBoard/format';
import { DateTimeField, cardClasses, inputClasses } from '../StatusBoard/atoms';
import {
  CORRECTION_KINDS,
  IMPACT_META,
  SUBJECT_LABELS,
  buildCorrection,
  causeLabel,
  errorMessage,
  factorBreakdown,
  kindLabel,
  readReviewDetail,
  REVIEW_THRESHOLD,
} from './assuranceShared';
import {
  ConfidenceChip,
  ReviewStateChip,
  ReasonField,
  InlineError,
  buttonClasses,
} from './assuranceAtoms';
import EvidenceMap from './EvidenceMap';

const OUTCOME_BUTTON =
  'inline-flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 min-h-[48px] text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

export function FactorTable({ factors, confidence }) {
  const { rows, total, confidence: computed } = factorBreakdown(factors);
  const shown = confidence ?? computed;
  return (
    <div>
      <table className="w-full text-sm" aria-label="Confidence breakdown">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-1.5 pr-3 font-semibold">Factor</th>
            <th className="py-1.5 pr-3 font-semibold">What we saw</th>
            <th className="py-1.5 font-semibold text-right">Points</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <tr>
            <td className="py-1.5 pr-3 text-slate-700">Start</td>
            <td className="py-1.5 pr-3 text-slate-500">Every record starts fully trusted</td>
            <td className="py-1.5 text-right tabular-nums text-slate-900">100</td>
          </tr>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} className="py-2 text-slate-500">
                No penalties recorded for this item.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.factor}>
              <td
                className="py-1.5 pr-3 text-slate-900 font-medium align-top"
                title={r.rule || undefined}
              >
                {r.label}
              </td>
              <td className="py-1.5 pr-3 text-slate-600 align-top">{r.detail || '—'}</td>
              <td className="py-1.5 text-right tabular-nums font-semibold text-slate-900 align-top">
                {r.penalty < 0 ? `−${Math.abs(r.penalty)}` : r.penalty}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-300">
            <td className="py-1.5 pr-3 font-semibold text-slate-900">Confidence</td>
            <td className="py-1.5 pr-3 text-xs text-slate-500">
              100 {total < 0 ? `− ${Math.abs(total)}` : ''} · below {REVIEW_THRESHOLD} is held for
              review
            </td>
            <td className="py-1.5 text-right tabular-nums font-bold text-slate-900">{shown}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function CorrectionForm({ item, busy, error, onCancel, onSubmit }) {
  const [kind, setKind] = useState('');
  const [fields, setFields] = useState({
    split_at: null,
    merge_with: '',
    type: '',
    trip_id: '',
    asset_key: '',
  });
  const [reason, setReason] = useState('');
  const [localError, setLocalError] = useState(null);
  const splitId = useId();
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = () => {
    const built = buildCorrection(kind, fields);
    if (built.error) return setLocalError(built.error);
    if (!reason.trim())
      return setLocalError('Give a reason — every correction is audited with one.');
    setLocalError(null);
    onSubmit({ kind, after: built.after, reason: reason.trim() });
  };

  return (
    <div
      role="group"
      aria-label="Correct the record"
      className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3"
    >
      <fieldset>
        <legend className="block text-sm font-medium text-slate-700 mb-1.5">
          How is it wrong?
        </legend>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CORRECTION_KINDS).map(([k, meta]) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              onClick={() => {
                setKind(k);
                setLocalError(null);
              }}
              className={`px-3 min-h-[44px] rounded-lg text-sm font-medium border ${kind === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-100'}`}
            >
              {meta.label}
            </button>
          ))}
        </div>
        {kind && <p className="mt-1.5 text-xs text-slate-500">{CORRECTION_KINDS[kind].help}</p>}
      </fieldset>

      {kind === 'split' && (
        <div>
          <label htmlFor={splitId} className="block text-sm font-medium text-slate-700 mb-1">
            Split at
          </label>
          <DateTimeField
            id={splitId}
            value={fields.split_at}
            onChange={set('split_at')}
            placeholder="When the second record starts"
          />
        </div>
      )}
      {kind === 'merge' && (
        <label className="block text-sm font-medium text-slate-700">
          Merge with (record id)
          <input
            className={`${inputClasses} mt-1`}
            value={fields.merge_with}
            onChange={set('merge_with')}
            placeholder="The other event or trip id"
          />
        </label>
      )}
      {kind === 'reclassify' && (
        <label className="block text-sm font-medium text-slate-700">
          Correct type
          <input
            className={`${inputClasses} mt-1 font-mono`}
            value={fields.type}
            onChange={set('type')}
            placeholder="e.g. TRIP.ARRIVED_DESTINATION"
          />
        </label>
      )}
      {kind === 'reassign' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm font-medium text-slate-700">
            To trip (id or reference)
            <input
              className={`${inputClasses} mt-1`}
              value={fields.trip_id}
              onChange={set('trip_id')}
              placeholder={item.trip_id || 'Trip id'}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Or to truck (registration)
            <input
              className={`${inputClasses} mt-1 font-mono`}
              value={fields.asset_key}
              onChange={set('asset_key')}
              placeholder="JX12KLGP"
            />
          </label>
        </div>
      )}

      <ReasonField value={reason} onChange={setReason} />
      <InlineError message={localError || error} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={buttonClasses.secondary}>
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !kind}
          className={buttonClasses.primary}
        >
          {busy
            ? 'Saving…'
            : kind
              ? `Save correction: ${CORRECTION_KINDS[kind].label.toLowerCase()}`
              : 'Save correction'}
        </button>
      </div>
    </div>
  );
}

function ReasonForm({ title, confirmLabel, required, busy, error, onCancel, onSubmit }) {
  const [reason, setReason] = useState('');
  return (
    <div
      role="group"
      aria-label={title}
      className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3"
    >
      <ReasonField value={reason} onChange={setReason} required={required} autoFocus />
      <InlineError message={error} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={buttonClasses.secondary}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSubmit(reason.trim())}
          disabled={busy || (required && !reason.trim())}
          className={buttonClasses.primary}
        >
          {busy ? 'Saving…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}

function Resolution({ item }) {
  const r = item.resolution || {};
  return (
    <p className="text-sm text-slate-700">
      <ReviewStateChip state={item.state} /> {item.resolved_by && <>by {item.resolved_by} </>}
      {item.resolved_at && <>at {fmtDateTime(item.resolved_at)}</>}
      {r.reason && <> — “{r.reason}”</>}
    </p>
  );
}

/**
 * @param {{item:Object, canTriage:boolean, pending:null|'accept'|'correct'|'dismiss',
 *          setPending:(p)=>void, busy:boolean, actionError:string|null,
 *          onResolve:(outcome:'accept'|'correct'|'dismiss', payload:Object)=>void,
 *          onBack?:()=>void, now:number}} props
 */
export default function ReviewItemPanel({
  item,
  canTriage,
  pending,
  setPending,
  busy,
  actionError,
  onResolve,
  onBack,
  now,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    getReviewItem(item.id)
      .then((res) => alive && setDetail(readReviewDetail(res)))
      .catch((err) => alive && setLoadError(errorMessage(err, 'Could not load the evidence.')))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [item.id]);

  const full = { ...item, ...(detail?.item || {}) };
  const factors = detail?.factors?.length ? detail.factors : full.factors;
  const open = (full.state || 'open') === 'open';
  const impact = IMPACT_META[full.impact] || IMPACT_META.report;
  const subject = detail?.subject || null;
  const related = detail?.relatedEvents || [];
  const corrections = detail?.corrections || pickList(full, 'corrections');

  return (
    <article className="space-y-4" aria-label="Review item">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className={`${buttonClasses.secondary} min-h-[44px] lg:hidden`}
        >
          <FaArrowLeft size={11} /> Back to the queue
        </button>
      )}
      <section className={`${cardClasses} p-4`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {kindLabel(full.kind)}
            </p>
            <h2 className="mt-0.5 text-lg font-bold text-slate-900 break-words">
              {SUBJECT_LABELS[full.subject_type] || full.subject_type}{' '}
              <span className="font-mono text-base">
                {subject?.type || subject?.reference || full.subject_id}
              </span>
            </h2>
            <p className="text-sm text-slate-600 mt-0.5">
              {full.asset_key && (
                <span className="font-mono">{String(full.asset_key).replace(/^reg:/, '')}</span>
              )}
              {full.trip_id && (
                <>
                  {' · '}
                  <Link
                    to={`/lexistar/trips/${full.trip_id}`}
                    className="text-primary-700 hover:underline"
                  >
                    {full.trip_reference || 'Open trip'}
                  </Link>
                </>
              )}
              {(subject?.event_time || subject?.at) && (
                <> · {fmtDateTime(subject.event_time || subject.at)}</>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ConfidenceChip confidence={full.confidence} />
            {!open && <ReviewStateChip state={full.state} />}
          </div>
        </div>
        <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-[11px] text-slate-500 uppercase tracking-wider">Impact</dt>
            <dd className="font-semibold text-slate-900" title={impact.help}>
              {impact.label}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] text-slate-500 uppercase tracking-wider">Waiting</dt>
            <dd className="font-semibold text-slate-900 tabular-nums">
              {fmtDuration(secondsSince(full.opened_at, now))}
            </dd>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <dt className="text-[11px] text-slate-500 uppercase tracking-wider">Cause</dt>
            <dd className="font-semibold text-slate-900 break-words">
              {causeLabel(full.cause_key)}
            </dd>
          </div>
        </dl>
      </section>

      <section className={`${cardClasses} p-4`} aria-label="Map evidence">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
          Evidence · five minutes either side
        </h3>
        {loading ? (
          <div className="h-[240px] rounded-lg bg-slate-100 animate-pulse" aria-hidden="true" />
        ) : loadError ? (
          <InlineError message={loadError} />
        ) : (
          <EvidenceMap
            positions={detail?.positions}
            subject={subject}
            geofence={detail?.geofence}
          />
        )}
      </section>

      <section className={`${cardClasses} p-4`} aria-label="Why it needs review">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
          Why it needs review
        </h3>
        <FactorTable factors={factors} confidence={full.confidence} />
      </section>

      {(related.length > 0 || corrections.length > 0) && (
        <section className={`${cardClasses} p-4 grid grid-cols-1 md:grid-cols-2 gap-4`}>
          {related.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Related events
              </h3>
              <ul className="space-y-1 text-sm" aria-label="Related events">
                {related.map((e) => (
                  <li key={e.id} className="flex items-baseline justify-between gap-2">
                    <span className="font-mono text-xs text-slate-800 truncate">{e.type}</span>
                    <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
                      {fmtTime(e.event_time || e.at)}
                      {e.confidence != null && ` · ${e.confidence}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {corrections.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Earlier corrections
              </h3>
              <ul className="space-y-1.5 text-sm" aria-label="Earlier corrections">
                {corrections.map((c) => (
                  <li key={c.id}>
                    <span className="font-semibold text-slate-900">
                      {CORRECTION_KINDS[c.kind]?.label || c.kind}
                    </span>
                    <span className="text-slate-600"> — “{c.reason}”</span>
                    <span className="block text-xs text-slate-500">
                      {c.created_by} · {fmtDateTime(c.created_at)}
                      {c.superseded_by && ' · superseded'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <section
        className={`${cardClasses} p-4 ${
          open && canTriage && !pending
            ? 'sticky bottom-3 z-10 shadow-lg lg:static lg:shadow-sm' // thumb reach on a phone
            : ''
        }`}
        aria-label="Outcome"
      >
        {!open ? (
          <Resolution item={full} />
        ) : !canTriage ? (
          <p className="text-sm text-slate-500">
            Resolving review items needs the{' '}
            <span className="font-mono">lexistar.review.triage</span> permission.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onResolve('accept', {})}
                disabled={busy}
                className={`${OUTCOME_BUTTON} bg-slate-900 text-white hover:bg-slate-800`}
              >
                <FaCheck size={12} /> Accept as recorded
                <kbd className="hidden lg:inline ml-1 px-1.5 py-0.5 rounded bg-white/15 text-[10px] font-mono">
                  A
                </kbd>
              </button>
              <button
                type="button"
                onClick={() => setPending('correct')}
                aria-pressed={pending === 'correct'}
                disabled={busy}
                className={`${OUTCOME_BUTTON} bg-surface text-slate-900 border border-slate-300 hover:bg-slate-50`}
              >
                <FaPen size={11} /> Correct…
                <kbd className="hidden lg:inline ml-1 px-1.5 py-0.5 rounded bg-black/5 text-[10px] font-mono">
                  C
                </kbd>
              </button>
              <button
                type="button"
                onClick={() => setPending('dismiss')}
                aria-pressed={pending === 'dismiss'}
                disabled={busy}
                className={`${OUTCOME_BUTTON} bg-surface text-slate-900 border border-slate-300 hover:bg-slate-50`}
              >
                <FaTimes size={12} /> Dismiss…
                <kbd className="hidden lg:inline ml-1 px-1.5 py-0.5 rounded bg-black/5 text-[10px] font-mono">
                  D
                </kbd>
              </button>
            </div>
            {pending !== 'accept_note' && (
              <button
                type="button"
                onClick={() => setPending('accept_note')}
                className="text-xs font-medium text-primary-700 hover:underline"
              >
                Accept with a note…
              </button>
            )}
            {pending === 'accept_note' && (
              <ReasonForm
                title="Accept with a note"
                confirmLabel="Accept as recorded"
                required={false}
                busy={busy}
                error={actionError}
                onCancel={() => setPending(null)}
                onSubmit={(reason) => onResolve('accept', reason ? { reason } : {})}
              />
            )}
            {pending === 'dismiss' && (
              <ReasonForm
                title="Dismiss"
                confirmLabel="Dismiss the item"
                required
                busy={busy}
                error={actionError}
                onCancel={() => setPending(null)}
                onSubmit={(reason) => onResolve('dismiss', { reason })}
              />
            )}
            {pending === 'correct' && (
              <CorrectionForm
                item={full}
                busy={busy}
                error={actionError}
                onCancel={() => setPending(null)}
                onSubmit={(payload) => onResolve('correct', payload)}
              />
            )}
            {!pending && <InlineError message={actionError} />}
          </div>
        )}
      </section>
    </article>
  );
}
